import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Shield, Lock, FileCheck2, Server, Users, History } from "lucide-react";

const SECTIONS = [
  {
    icon: Lock,
    title: "Encryption",
    body: "Data in transit is protected with TLS 1.3 (HSTS enforced). Passwords are hashed with bcrypt. JWT secrets and storage keys are kept in a private secret store, never in source code.",
  },
  {
    icon: Users,
    title: "Identity & Access",
    body: "Role-based access control (property manager, NTN staff, applicant). Two-factor authentication (TOTP) available. Refresh-token rotation with reuse detection. Brute-force account lockout (5 fails / 15 min). Idle session timeout.",
  },
  {
    icon: History,
    title: "Audit logging",
    body: "Every sensitive action — login, screening submission, report status change, file upload, file download, role change — is recorded in an append-only audit log with actor, IP, user-agent, and timestamp. Available to each user under Account → Audit log.",
  },
  {
    icon: Server,
    title: "Hosting & infrastructure",
    body: "Hosted on a hardened Kubernetes cluster behind Cloudflare. Database backups taken daily and encrypted at rest. Object storage uses session-scoped, server-issued keys; no presigned URLs.",
  },
  {
    icon: FileCheck2,
    title: "Compliance posture",
    body: "Built to map cleanly to the Fair Credit Reporting Act (FCRA) and industry Trust Service Criteria (Security, Availability, Confidentiality, Processing Integrity, Privacy). Independent attestation in progress.",
  },
  {
    icon: Shield,
    title: "Vulnerability disclosure",
    body: "Found a security issue? Email security@ntnverify.com. We acknowledge within 2 business days and will not pursue action against good-faith research that follows our policy.",
  },
];

export default function SecurityPage() {
  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <header className="border-b border-[#0A192F]/10 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <nav className="flex gap-6 text-sm">
            <Link to="/security" className="text-[#0A192F] font-medium">Security</Link>
            <Link to="/privacy" className="text-[#4A5568]">Privacy</Link>
            <Link to="/terms" className="text-[#4A5568]">Terms</Link>
          </nav>
        </div>
      </header>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="font-overline text-[#4A5568] mb-4">Trust & security</div>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-[#0A192F] max-w-4xl">
          Defensible by design.
        </h1>
        <p className="mt-6 text-lg text-[#4A5568] max-w-2xl leading-relaxed">
          NTN handles sensitive applicant PII every day. Here's how we protect it — at the platform layer,
          at the operational layer, and on your behalf as a property manager.
        </p>
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0A192F]/10 border border-[#0A192F]/10">
          {SECTIONS.map((s) => (
            <div key={s.title} className="bg-white p-8" data-testid={`sec-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <s.icon className="h-7 w-7 text-[#0A192F] mb-5" strokeWidth={1.5} />
              <h2 className="font-serif text-xl text-[#0A192F] mb-3">{s.title}</h2>
              <p className="text-sm text-[#4A5568] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>
      <footer className="border-t border-[#0A192F]/10 py-8 bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-xs text-[#4A5568] font-mono">
          © {new Date().getFullYear()} NATIONAL TENANT NETWORK · LAST UPDATED FEB 2026
        </div>
      </footer>
    </div>
  );
}
